import { useCallback } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useProjectStore } from '../stores/projectStore';
import { useTaskStore } from '../stores/taskStore';
import { useDataPersistence, setPersistedGroups, getPersistedGroups } from './useDataPersistence';
import { unifiedStorage } from '../services/unifiedStorage';
import { projectApi } from '../services/projectApi';
import { isBackendAssetUrl, normalizeAssetUrl } from '../utils/assetUtils';
import { useGroupStore } from '../stores/groupStore';
import { useCanvasStore } from '../stores/canvasStore';
import { generateApi } from '../services/generateApi';
import { findStrategy, GenerationContext } from '../services/generationStrategies';
import { eventBus, CanvasEvents } from '../utils/eventBus';
import { NodeStatus, NodeType, AppNode } from '../types';

function reconcileNodes(nodes: AppNode[]): AppNode[] {
    return nodes.map((node) => {
        if (node.status !== NodeStatus.WORKING) return node;
        const d = node.data;
        const hasResult = d.image || (d.images && d.images.length > 0) ||
            d.videoUri || (d.videoUris && d.videoUris.length > 0) ||
            d.audioUri || d.panoramaOutputUrl || d.analysis;
        if (node.type === NodeType.MEDIA_UPLOAD) {
            return { ...node, status: NodeStatus.IDLE };
        }
        if (hasResult) {
            return { ...node, status: NodeStatus.SUCCESS };
        }
        const pendingTask = useTaskStore.getState().getTaskByNodeId(node.id);
        if (pendingTask && pendingTask.status === 'RUNNING') {
            return { ...node, status: NodeStatus.WORKING, data: { ...node.data, progress: '恢复任务中...' } };
        }
        if (d.taskId || d.pendingTaskId) {
            return { ...node, status: NodeStatus.WORKING, data: { ...node.data, progress: '正在检查任务状态...', _needsTaskRecovery: true } };
        }
        return { ...node, status: NodeStatus.IDLE, data: { ...node.data, error: '任务因页面刷新中断' } };
    });
}

function reconcileAndNormalize(nodes: AppNode[]): AppNode[] {
    const normalized = nodes.map(node => {
        const normalizedData = { ...node.data };
        if (normalizedData.image && isBackendAssetUrl(normalizedData.image)) {
            normalizedData.image = normalizeAssetUrl(normalizedData.image);
        } else if (typeof normalizedData.image === 'string' && normalizedData.image.includes('\\')) {
            normalizedData.image = normalizedData.image.replace(/\\/g, '/');
        }
        if (normalizedData.images && Array.isArray(normalizedData.images)) {
            normalizedData.images = normalizedData.images.map((img: string) =>
                isBackendAssetUrl(img) ? normalizeAssetUrl(img) : (typeof img === 'string' && img.includes('\\') ? img.replace(/\\/g, '/') : img)
            );
        }
        if (normalizedData.videoUri && isBackendAssetUrl(normalizedData.videoUri)) {
            normalizedData.videoUri = normalizeAssetUrl(normalizedData.videoUri);
        } else if (typeof normalizedData.videoUri === 'string' && normalizedData.videoUri.includes('\\')) {
            normalizedData.videoUri = normalizedData.videoUri.replace(/\\/g, '/');
        }
        if (normalizedData.videoUris && Array.isArray(normalizedData.videoUris)) {
            normalizedData.videoUris = normalizedData.videoUris.map((vid: string) =>
                isBackendAssetUrl(vid) ? normalizeAssetUrl(vid) : (typeof vid === 'string' && vid.includes('\\') ? vid.replace(/\\/g, '/') : vid)
            );
        }
        if (typeof normalizedData.audioUri === 'string') {
            if (isBackendAssetUrl(normalizedData.audioUri)) {
                normalizedData.audioUri = normalizeAssetUrl(normalizedData.audioUri);
            } else if (normalizedData.audioUri.includes('\\')) {
                normalizedData.audioUri = normalizedData.audioUri.replace(/\\/g, '/');
            }
        }
        if (typeof normalizedData.panoramaOutputUrl === 'string') {
            if (isBackendAssetUrl(normalizedData.panoramaOutputUrl)) {
                normalizedData.panoramaOutputUrl = normalizeAssetUrl(normalizedData.panoramaOutputUrl);
            } else if (normalizedData.panoramaOutputUrl.includes('\\')) {
                normalizedData.panoramaOutputUrl = normalizedData.panoramaOutputUrl.replace(/\\/g, '/');
            }
        }
        return { ...node, data: normalizedData };
    });

    return reconcileNodes(normalized);
}

async function recoverWorkingNodes(projectId: string) {
    const currentNodes = useNodeStore.getState().nodes;
    const { updateNode } = useNodeStore.getState();
    const workingNodes = currentNodes.filter(n =>
        n.status === NodeStatus.WORKING || n.data._needsTaskRecovery
    );

    if (workingNodes.length === 0) return;

    for (const node of workingNodes) {
        const taskId = node.data.taskId || node.data.pendingTaskId;
        if (!taskId) {
            const d = node.data;
            const hasResult = d.image || (d.images && d.images.length > 0) ||
                d.videoUri || (d.videoUris && d.videoUris.length > 0) ||
                d.audioUri || d.panoramaOutputUrl || d.analysis;
            if (hasResult) {
                updateNode(node.id, { progress: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.SUCCESS);
            } else {
                updateNode(node.id, { error: '任务信息丢失', progress: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.IDLE);
            }
            continue;
        }

        const pendingTask = useTaskStore.getState().getTaskByNodeId(node.id);
        if (pendingTask && pendingTask.status === 'RUNNING') continue;

        try {
            const data = await generateApi.getTaskStatus(taskId);

            if (data.status === 'success' && data.result) {
                const nodeType = node.type;
                const strategy = findStrategy(nodeType as any);
                const request: Record<string, any> = {
                    modelId: node.data.model,
                    prompt: node.data.prompt,
                    params: {
                        aspectRatio: node.data.aspectRatio,
                        resolution: node.data.resolution,
                        imageCount: node.data.imageCount,
                    },
                };

                if (strategy) {
                    const mockNode: AppNode = {
                        id: node.id,
                        type: nodeType as any,
                        x: 0, y: 0,
                        title: '',
                        status: NodeStatus.WORKING,
                        data: request,
                        inputs: [],
                    };

                    const rawResult = {
                        taskId,
                        status: 'success',
                        results: data.result.results,
                        error: data.result.error,
                        videoMetadata: data.result.videoMetadata,
                        analysis: data.result.analysis,
                    };

                    const ctx: GenerationContext = {
                        projectId,
                        onProgress: () => {},
                        onAssetGenerated: () => {},
                        onTaskCreated: () => {},
                    };

                    const updates = await strategy.processResult(mockNode, rawResult, ctx, request.prompt || '');
                    updateNode(node.id, { ...updates, progress: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.SUCCESS);

                    if (node.id.startsWith('agent-node-')) {
                        eventBus.emit(CanvasEvents.AGENT_TASK_COMPLETED, {
                            taskId,
                            nodeId: node.id,
                            status: 'SUCCESS',
                            updates,
                        });
                    }
                } else {
                    updateNode(node.id, { progress: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.SUCCESS);
                }
                useTaskStore.getState().removeTask(taskId);
            } else if (data.status === 'failed') {
                updateNode(node.id, { error: data.error || '任务失败', progress: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.IDLE);
                useTaskStore.getState().removeTask(taskId);
            } else if (data.status === 'pending' || data.status === 'running') {
                useTaskStore.getState().addTaskWithRequest(taskId, node.id, node.type || '', {
                    modelId: node.data.model,
                    prompt: node.data.prompt,
                    params: {
                        aspectRatio: node.data.aspectRatio,
                        resolution: node.data.resolution,
                    },
                });
                updateNode(node.id, { _needsTaskRecovery: false, progress: '生成中...' }, undefined, undefined, NodeStatus.WORKING);
            } else {
                updateNode(node.id, { error: '未知任务状态', progress: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.IDLE);
            }
        } catch (e: any) {
            console.error(`[Recovery] Failed to recover task for node ${node.id}:`, e);
            updateNode(node.id, { error: e.message || '恢复任务失败', progress: undefined, _needsTaskRecovery: false }, undefined, undefined, NodeStatus.IDLE);
        }
    }
}

export function useProjectNavigation(saveHistory: () => void) {
    const nodes = useNodeStore((s) => s.nodes);
    const connections = useNodeStore((s) => s.connections);
    const setNodes = useNodeStore((s) => s.setNodes);
    const setConnections = useNodeStore((s) => s.setConnections);
    const projects = useProjectStore((s) => s.projects);
    const currentProjectId = useProjectStore((s) => s.currentProjectId);
    const loadProjects = useProjectStore((s) => s.loadProjects);
    const createProject = useProjectStore((s) => s.createProject);
    const loadProject = useProjectStore((s) => s.loadProject);
    const saveCurrentProject = useProjectStore((s) => s.saveCurrentProject);
    const deleteProject = useProjectStore((s) => s.deleteProject);
    const setCurrentProject = useProjectStore((s) => s.setCurrentProject);

    const getThumbnail = useCallback(() => {
        const thumbnailNode = nodes.find(n => n.data.image);
        return thumbnailNode?.data.image || '';
    }, [nodes]);

    const loadWorkflow = async (id: string, transitionTo: () => Promise<void>) => {
        await transitionTo();
        saveHistory();

        let loadedFromCache = false;

        // 1. 先从 IndexedDB 读缓存（容量大，不会配额超限）
        let cachedNodes: any[] = [];
        let cachedConns: any[] = [];
        let cachedGroups: any[] = [];

        try {
            // 优先用 unifiedStorage.loadProjectCache（专门的 project cache）
            const projectCache = await unifiedStorage.loadProjectCache(id);
            if (projectCache && projectCache.nodes && projectCache.nodes.length > 0) {
                cachedNodes = projectCache.nodes;
                cachedConns = projectCache.connections || [];
                cachedGroups = projectCache.groups || [];
            }
        } catch {}

        if (cachedNodes.length === 0) {
            try {
                const canvasData = await unifiedStorage.getCanvas(id);
                if (canvasData && canvasData.nodes && canvasData.nodes.length > 0) {
                    cachedNodes = canvasData.nodes;
                    cachedConns = canvasData.edges || [];
                    cachedGroups = canvasData.groups || [];
                }
            } catch {}
        }

        // 2. 缓存命中：立即显示，不阻塞用户操作
        if (cachedNodes.length > 0) {
            const reconciledCache = reconcileNodes(cachedNodes);
            setNodes(reconciledCache);
            setConnections(cachedConns);
            setPersistedGroups(cachedGroups);
            useGroupStore.getState().rebuildGroupsFromNodesData(reconciledCache);
            loadedFromCache = true;
        }

        // 3. 并行请求后端（不阻塞缓存显示）
        try {
            const project = await projectApi.getProject(id);
            let loadedNodes = project.workflow_data?.nodes || [];
            let loadedConns = project.workflow_data?.connections || [];
            let loadedGroups = project.workflow_data?.groups || [];

            if (loadedNodes.length > 0) {
                const reconciledNodes = reconcileAndNormalize(loadedNodes);
                const nodeIds = new Set(reconciledNodes.map(n => n.id));
                const cleanedNodes = reconciledNodes.map(node => {
                    if (node.parentNode && !nodeIds.has(node.parentNode)) {
                        const { parentNode, extent, expandParent, ...rest } = node;
                        return rest;
                    }
                    return node;
                });

                setNodes(cleanedNodes);
                setConnections(loadedConns);
                setPersistedGroups(loadedGroups);
                setCurrentProject(project);
                useGroupStore.getState().rebuildGroupsFromNodesData(cleanedNodes);

                // 缓存到 IndexedDB（不再写 localStorage，避免配额超限）
                unifiedStorage.saveProjectCache(id, {
                    nodes: cleanedNodes,
                    connections: loadedConns,
                    groups: loadedGroups,
                }).catch(() => {});
                unifiedStorage.saveToIndexedDB('nodes', cleanedNodes);
                unifiedStorage.saveToIndexedDB('connections', loadedConns);
                unifiedStorage.saveToIndexedDB('groups', loadedGroups);
            } else if (loadedFromCache) {
                setCurrentProject(project);
            } else {
                setNodes([]);
                setConnections([]);
                setPersistedGroups([]);
                setCurrentProject(project);
            }

            recoverWorkingNodes(id);

            requestIdleCallback(() => {
                useCanvasStore.getState().rebuildSpatialIndex();
            });
        } catch (e: any) {
            const isNetworkError = e?.message?.includes('Failed to fetch') || e?.message?.includes('ERR_NETWORK') || e?.name === 'TypeError';
            if (!isNetworkError) {
                console.error('Failed to load project from backend, trying cache:', e);
            } else {
                console.warn('Network error loading project, falling back to cache');
            }

            // 后端失败：缓存已显示就无需再读，没缓存则尝试 IndexedDB
            if (!loadedFromCache) {
                try {
                    const cached = await unifiedStorage.loadProjectCache(id);
                    if (cached && cached.nodes && cached.nodes.length > 0) {
                        const cachedNodes = reconcileNodes(cached.nodes);
                        setNodes(cachedNodes);
                        setConnections(cached.connections || []);
                        setPersistedGroups(cached.groups || []);
                        setCurrentProject({
                            id,
                            name: '离线缓存恢复',
                            workflow_data: cached,
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                        });
                        useGroupStore.getState().rebuildGroupsFromNodesData(cachedNodes);
                    }
                } catch (cacheErr) {
                    console.error('Failed to load from cache:', cacheErr);
                }
            }

            recoverWorkingNodes(id);

            requestIdleCallback(() => {
                useCanvasStore.getState().rebuildSpatialIndex();
            });
        }
    };

    const handleCreateProject = async (title: string, transitionTo: () => Promise<void>) => {
        await transitionTo();
        try {
            const project = await createProject(title);
            setNodes([]);
            setConnections([]);
            setPersistedGroups([]);
            return project;
        } catch (e) {
            console.error('Failed to create project', e);
            return null;
        }
    };

    const handleBackToProjects = async (transitionFrom: () => Promise<void>) => {
        // 保存智能体聊天状态到本地缓存
        try {
            const { useAgentStore } = await import('@/stores/agentStore');
            useAgentStore.getState().saveToLocal();
        } catch (e) {
            console.warn('Failed to save agent state on back to projects:', e);
        }

        await transitionFrom();
        try {
            if (currentProjectId) {
                // 项目缓存只写 IndexedDB，不再写 localStorage（避免配额超限）
                if (nodes.length > 0) {
                    unifiedStorage.saveToIndexedDB('nodes', nodes);
                    unifiedStorage.saveToIndexedDB('connections', connections);
                    unifiedStorage.saveToIndexedDB('groups', getPersistedGroups());
                }
                try {
                    if (nodes.length > 0) {
                        await projectApi.updateProject(currentProjectId, {
                            thumbnail: getThumbnail(),
                            workflow_data: { nodes, connections, groups: getPersistedGroups() }
                        });
                    }
                } catch (saveErr) {
                    console.error('保存到后端失败，但本地缓存已保存:', saveErr);
                }
            }
            await loadProjects();
        } catch (e) {
            console.error('Failed to save project', e);
        }
    };

    const deleteWorkflow = async (id: string) => {
        try {
            await deleteProject(id);
        } catch (e) {
            console.error('Failed to delete project', e);
        }
    };

    const renameWorkflow = (id: string, newTitle: string, workflows: any[], setWorkflows: (fn: (prev: any[]) => any[]) => void) => {
        setWorkflows(prev => prev.map(w => w.id === id ? { ...w, title: newTitle } : w));
    };

    return {
        loadWorkflow,
        handleCreateProject,
        handleBackToProjects,
        deleteWorkflow,
        renameWorkflow,
        getThumbnail,
    };
}
